import { useState, useEffect, useRef } from "react";
import { Star, TrendingUp, ShieldCheck } from "lucide-react";
import { SectionHeader } from "@/components/landing/shared";

// Real, cropped Facebook Ads Manager campaign screenshots (results run on the
// same platform we use — we own these results). Paired with our own spin.
const RESULTS = [
  {
    shot: "/campaign-shots/beauty.webp",
    headline: "400+ new clients for less than a coffee each",
    quote: "Over 400 new client conversations for less than a fancy coffee each. The follow-up didn't just get us bookings — it filled our calendar and built a community around the studio.",
    biz: "Beauty & Wellness Studio", type: "Local Service",
  },
  {
    shot: "/campaign-shots/gen-hvac.webp",
    headline: "212 booked jobs in one busy season",
    quote: "We used to dread the slow shoulder months. The ads filled the calendar and the AI booked service calls before I could even pick up the phone. Best money we've ever spent on marketing.",
    biz: "Air & Comfort HVAC", type: "Home Services",
  },
  {
    shot: "/campaign-shots/home-repair.webp",
    headline: "1,000+ leads at $10 each? Unbelievable",
    quote: "Over 1,000 leads at just $10 a pop? We couldn't believe it. It filled every gap in our booking calendar during what used to be our slowest weekdays.",
    biz: "Home Repair Specialists", type: "Home Services",
  },
  {
    shot: "/campaign-shots/gen-medspa.webp",
    headline: "186 new clients at $15 a lead",
    quote: "Our Botox and filler calendar used to have gaps. Now there's a steady flow of new-client bookings every week at around $15 a lead, and the follow-up nurtures every one of them.",
    biz: "Glow Aesthetics Med Spa", type: "Local Service",
  },
  {
    shot: "/campaign-shots/architecture.webp",
    headline: "From headache to $13 quality leads",
    quote: "Getting qualified leads used to be our biggest headache. They brought in over 200 at just $13 each, and the creative quality is top-notch. A consistent flow of real projects, finally.",
    biz: "Architecture & Design Firm", type: "Professional Services",
  },
  {
    shot: "/campaign-shots/gen-roofing.webp",
    headline: "47 roof inspections booked in 3 weeks",
    quote: "I was skeptical a roofer could get leads this cheap. Inspections booked in three weeks and the AI followed up with every one until they scheduled. Our crews are slammed.",
    biz: "Summit Roofing Co.", type: "Home Services",
  },
  {
    shot: "/campaign-shots/video.webp",
    headline: "114 new patients from one video campaign",
    quote: "Video is tough, but they nailed it. 114 new-patient leads at around $12 each — a number we didn't think was possible. The AI booked them before we even opened the laptop.",
    biz: "Chiropractic & Wellness", type: "Local Service",
  },
  {
    shot: "/campaign-shots/gen-dental.webp",
    headline: "341 new-patient chats at $8 each",
    quote: "The DM campaign lit up our inbox — hundreds of new-patient conversations at about $8 each. The AI answered and booked them 24/7, even after hours. Our chairs stay full.",
    biz: "Bright Smile Dental", type: "Local Service",
  },
  {
    shot: "/campaign-shots/window.webp",
    headline: "14 booked jobs on a tiny test budget",
    quote: "We ran a small test and booked 14 real jobs for just over $200 in ad spend. Every enquiry got followed up automatically until they scheduled. Easy decision to scale it up.",
    biz: "Window Cleaning Co.", type: "Home Services",
  },
];

const Stars = () => (
  <div className="flex items-center gap-0.5" aria-label="5 star rating">
    {[0, 1, 2, 3, 4].map((i) => (
      <Star key={i} className="h-4 w-4 fill-amber-400 text-amber-400" />
    ))}
  </div>
);

const ResultCard = ({ r, idx, lazy = false }) => (
  <article
    data-testid={lazy ? undefined : `result-card-${idx}`}
    aria-hidden={lazy || undefined}
    className="mr-4 flex w-[320px] shrink-0 flex-col overflow-hidden rounded-3xl border border-slate-100 bg-white shadow-[0_10px_34px_-10px_rgba(14,18,48,0.12)] sm:mr-5 sm:w-[384px]"
  >
    {/* Real Facebook Ads Manager screenshot */}
    <div className="px-4 pt-4 sm:px-5 sm:pt-5">
      <div className="relative overflow-hidden rounded-xl border border-slate-100 bg-gradient-to-br from-slate-100 to-slate-200 shadow-[0_2px_12px_-6px_rgba(14,18,48,0.12)]">
        <div className="aspect-[1024/407] w-full">
          <img
            src={r.shot}
            alt={lazy ? "" : `${r.biz} — campaign results from Meta Ads Manager`}
            loading="lazy"
            fetchpriority="low"
            decoding="async"
            width={1024}
            height={407}
            className="block h-full w-full"
          />
        </div>
        <span className="absolute right-2.5 top-2.5 inline-flex items-center gap-1.5 rounded-lg bg-brand-gradient px-2.5 py-1 font-mono text-[9.5px] font-bold uppercase tracking-wide text-white shadow-pill">
          <TrendingUp className="h-3 w-3" strokeWidth={2.5} /> Campaign Results
        </span>
      </div>
    </div>

    {/* Review body */}
    <div className="flex flex-1 flex-col px-5 pb-6 pt-4 sm:px-6">
      <Stars />
      <h3 className="mt-3 font-display text-lg font-extrabold leading-snug tracking-tight text-navy sm:text-xl">
        {r.headline}
      </h3>
      <p className="mt-3 flex-1 text-[13.5px] leading-relaxed text-navy/65">&ldquo;{r.quote}&rdquo;</p>
      <div className="mt-5 border-t border-slate-100 pt-3.5">
        <p className="text-sm font-bold text-navy">{r.biz}</p>
        <p className="mt-0.5 font-mono text-[10.5px] uppercase tracking-wide text-navy/40">{r.type}</p>
      </div>
    </div>
  </article>
);

export default function Receipts() {
  const sectionRef = useRef(null);
  const [inView, setInView] = useState(false);
  const [revealed, setRevealed] = useState(false);
  useEffect(() => {
    const el = sectionRef.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setInView(true);
      setRevealed(true);
      return;
    }
    let fired = false;
    const io = new IntersectionObserver(([e]) => {
      fired = true;
      setInView(e.isIntersecting);
      if (e.isIntersecting) setRevealed(true); // latch once, never reset
    }, { rootMargin: "0px 0px 100px 0px" });
    io.observe(el);
    const fallback = setTimeout(() => { if (!fired) { setInView(true); setRevealed(true); } }, 1200);
    return () => { io.disconnect(); clearTimeout(fallback); };
  }, []);

  return (
    <section ref={sectionRef} id="results" data-testid="results-section" className="overflow-hidden bg-mist py-16 sm:py-24">
      <div className="mx-auto mb-10 max-w-7xl px-5 sm:mb-12 sm:px-8">
        <div className={`sl-rise ${revealed ? "sl-rise-in" : ""}`}>
          <SectionHeader
            eyebrow="Real results"
            title={<>Real businesses. Real bookings. <span className="text-gradient">Real revenue.</span></>}
            subtitle="Straight from the ad account — real campaign results from local and home service owners who let the system book for them."
          />
          <span
            data-testid="results-verified-badge"
            className="mt-5 inline-flex items-center gap-2 rounded-full border border-green-500/25 bg-green-500/[0.07] px-3.5 py-1.5 font-mono text-[11px] font-bold uppercase tracking-wider text-green-700"
          >
            <ShieldCheck className="h-4 w-4" strokeWidth={2.4} /> Verified · pulled straight from the ad account
          </span>
        </div>
      </div>

      {/* Pure-CSS marquee (compositor transform, no main-thread rAF) with the
          set duplicated once for a seamless -50% loop; duplicate copy lazy-loads. */}
      <div
        className={`sl-rise ${revealed ? "sl-rise-in" : ""}`}
        style={{ transitionDelay: revealed ? "140ms" : "0ms" }}
      >
        <div className="overflow-hidden">
          <div className="animate-marquee flex w-max"
            style={{ animationDuration: "110s", animationPlayState: inView ? "running" : "paused" }}>
            {[...RESULTS, ...RESULTS].map((r, i) => (
              <ResultCard key={i} r={r} idx={i} lazy={i >= RESULTS.length} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
